import React from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
const THUMB_HUE_STEP = 47;
const HUE_WHEEL_MAX = 360;
const THUMB_SAT = 55;
const THUMB_LUM = 55;

/**
 * Drag-and-drop file picker + selected-files list + ADD-MORE button.
 *
 * Wraps the dashed dropzone (with drag-active visual state), the hidden
 * <input type="file"> the label clicks into, and the selected-files
 * removal list. Purely presentational — parent owns state + handlers.
 */
export const ArtworkDropzone = ({
  files,
  isPageMaker,
  isDragActive,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onFileSelect,
  onRemoveFile,
}) => (
  <div>
    <div
      data-testid="upload-artwork-dropzone"
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
        isDragActive
          ? 'border-[#4A7C59] bg-[#4A7C59]/10 ring-2 ring-[#4A7C59]/30'
          : 'border-[#2F5D3A]/30 bg-white hover:bg-[#4A7C59]/5 hover:border-[#4A7C59]'
      }`}
    >
      <input
        type="file"
        id="fileInput"
        multiple
        accept=".pdf,.ai,.eps,.psd,.jpg,.jpeg,.png,.svg"
        onChange={onFileSelect}
        className="hidden"
      />
      <label
        htmlFor="fileInput"
        className="cursor-pointer flex flex-col items-center w-full"
        data-testid="upload-artwork-dropzone-label"
      >
        <Upload className={`w-12 h-12 mb-3 ${isDragActive ? 'text-[#4A7C59]' : 'text-gray-400'}`} />
        <span className="text-lg font-medium text-gray-700 mb-2">
          {isDragActive
            ? 'Drop your file to upload'
            : isPageMaker
              ? 'Add Designs'
              : 'Choose Files'}
        </span>
        <span className="text-sm text-gray-500">
          {isPageMaker
            ? 'Upload as many different designs as you like'
            : 'or drag and drop files here'}
        </span>
        <span className="text-xs text-gray-400 mt-2">
          Supported formats: PDF, AI, EPS, PSD, JPG, PNG, SVG
        </span>
      </label>
    </div>

    {files.length > 0 && (
      <div className="mt-4 space-y-2">
        <h3 className="font-semibold text-gray-700" data-testid="upload-selected-files-header">
          {isPageMaker ? `Designs on Page (${files.length})` : `Selected Files (${files.length}):`}
        </h3>
        {files.map((file, index) => (
          <div
            key={`${file.name}-${file.size}`}
            className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
          >
            <div className="flex items-center space-x-3">
              {isPageMaker && (
                <div
                  className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white"
                  style={{
                    backgroundColor: `hsl(${(index * THUMB_HUE_STEP) % HUE_WHEEL_MAX}, ${THUMB_SAT}%, ${THUMB_LUM}%)`,
                  }}
                >
                  {index + 1}
                </div>
              )}
              <FileText className="w-5 h-5 text-[#4A7C59]" />
              <span className="text-sm font-medium text-gray-700">{file.name}</span>
              <span className="text-xs text-gray-500">
                ({(file.size / BYTES_PER_MB).toFixed(2)} MB)
              </span>
            </div>
            <button
              onClick={() => onRemoveFile(index)}
              data-testid={`upload-remove-file-${index}`}
              className="text-red-500 hover:text-red-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>
    )}

    <Button
      onClick={() => document.getElementById('fileInput').click()}
      className="w-full mt-4 bg-[#4A7C59] hover:bg-[#3d6849] text-white"
      data-testid="upload-add-more-btn"
    >
      {isPageMaker ? 'ADD ANOTHER DESIGN' : 'ADD MORE'}
    </Button>
  </div>
);

export default ArtworkDropzone;
